import { MAIN_TABS, VERB_TABS } from '$lib/constants';
import type { Usage, Verb, Hint } from '$lib/typedefs';
import { isLoggedInURL } from '$lib/urlutils.svelte';

export const appState = $state({
	isLoggedIn: null as boolean | null,
	verb: null as Verb | null,
	mainTab: MAIN_TABS.HOME,
	verbTab: VERB_TABS.CONJUGATION,
	quiz: []
});

export async function checkLoginStatus() {
	if (appState.isLoggedIn != null) {
		console.log('loggedin State: ' + appState.isLoggedIn);
		return;
	}

	try {
		const res = await fetch(isLoggedInURL(), {
			credentials: 'include' // important if cookies/session are needed
		});
		if (res.status === 200) {
			console.log('logged in');
			appState.isLoggedIn = true;
			console.log('isLoggedIn ' + appState.isLoggedIn);
		} else {
			const data = await res.json().catch(() => ({}));
			if (data?.error === 'notAuthorized') {
				appState.isLoggedIn = false;
			} else {
				console.error('Unexpected response', res.status, data);
				appState.isLoggedIn = false; // fallback
			}
		}
	} catch (err) {
		console.error('Error checking login status:', err);
		appState.isLoggedIn = false;
	}
}

export async function sha256Hex(payload: string) {
	const encoder = new TextEncoder();
	const data = encoder.encode(payload);
	const hashBuffer = await crypto.subtle.digest('SHA-256', data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sendDeleteHint(id: string) {
	return await fetch(`/api/hint/${id}`, {
		method: 'DELETE'
	});
}

export async function sendSaveHint(hint: Hint) {
	const body = JSON.stringify({ hintText: hint.hintText, example: hint.example });
	const payloadHash = await sha256Hex(body);

	return await fetch(`/api/hint/${hint.id}`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
			'X-Amz-Content-Sha256': payloadHash
		},
		body: body,
		credentials: 'include' // include cookies/session if needed
	});
}

export async function getHints(): Promise<{ hints: Hint[] }> {
	const res = await fetch('/api/hint');

	if (!res.ok) {
		return { hints: [] };
	}

	const hints: Hint[] = await res.json();

	return { hints };
}

export async function sendAddHint(hintText: string, example: string) {
	const body = JSON.stringify({ hintText: hintText, example: example });
	const payloadHash = await sha256Hex(body);

	const response = await fetch(`/api/hint`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Amz-Content-Sha256': payloadHash
		},
		body: body,
		credentials: 'include' // include cookies/session if needed
	});
	return response;
}

export async function sendSaveVerbHint(searchName: string, hintId: string) {
	try {
		const body = JSON.stringify({ hintId: hintId });
		const payloadHash = await sha256Hex(body);

		const response = await fetch(`/api/verb/${searchName}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
				'X-Amz-Content-Sha256': payloadHash
			},
			body: body,
			credentials: 'include' // include cookies/session if needed
		});

		if (!response.ok) {
			const errorBody = await response.json();
			throw new Error(`Failed to save verb hint: ${response.status} ${errorBody.errorMsg}`);
		}
	} catch (err) {
		console.error('Error saving verb hint:', err);
	}
}

export async function sendSaveUsage(usage: Usage) {
	try {
		const body = JSON.stringify({ text: usage.text });
		const payloadHash = await sha256Hex(body);

		const response = await fetch(`/api/usage/${usage.id}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
				'X-Amz-Content-Sha256': payloadHash
			},
			body: body,
			credentials: 'include' // include cookies/session if needed
		});

		if (!response.ok) {
			const errorBody = await response.json();
			throw new Error(`Failed to save usasge: ${response.status} ${errorBody.errorMsg}`);
		}
	} catch (err) {
		console.error('Error saving usage:', err);
	}
}

export async function sendAddUsage(searchName: string, text: string): Promise<string> {
	let newID = '';
	try {
		const body = JSON.stringify({ searchName: searchName, text: text });
		const payloadHash = await sha256Hex(body);

		const response = await fetch(`/api/usage`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Amz-Content-Sha256': payloadHash
			},
			body: body,
			credentials: 'include' // include cookies/session if needed
		});

		if (!response.ok) {
			const errorBody = await response.json();
			throw new Error(`Failed to add usage: ${response.status} ${errorBody.errorMsg}`);
		}
		interface NewIDResponse {
			id: string;
		}

		const data: NewIDResponse = await response.json();
		newID = data.id; // type is now guaranteed string
	} catch (err) {
		console.error('Error adding usage:', err);
	}
	return newID;
}

export async function sendDeleteUsage(id: string) {
	try {
		const response = await fetch(`/api/usage/${id}`, {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json'
			},
			credentials: 'include' // include cookies/session if needed
		});

		if (!response.ok) {
			const errorBody = await response.json();
			throw new Error(`Failed to save usage: ${response.status} ${errorBody.errorMsg}`);
		}
	} catch (err) {
		console.error('Error deleting usage:', err);
	}
}
